import { useEffect, useState } from "react";
import { MUSIC_RESOURCES_FIELDS } from "../pages/parishMusicResourcesOptions";

const slug = (s) =>
  String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function Chips({ k, options, value, onToggle }) {
  const arr = Array.isArray(value) ? value : value ? [String(value)] : [];
  const legacy = arr.filter((v) => !options.includes(v));
  return (
    <div className="pp-multi" data-testid={`mr-${k}`}>
      {[...options, ...legacy].map((o) => (
        <button
          type="button"
          key={o}
          className={`pp-chip${arr.includes(o) ? " on" : ""}`}
          data-testid={`mr-${k}-${slug(o)}`}
          onClick={() => onToggle(o)}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

export default function MusicResourcesSection({ data, catalog, update }) {
  const d = data || {};
  const [loadedCatalog, setCatalog] = useState(catalog || null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (loadedCatalog) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/parish-profile/music-resources/catalog`;
    fetch(url, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j) => setCatalog(j))
      .catch((e) => setErr(`Could not load catalog (${e}).`));
  }, [loadedCatalog]);

  const cat = loadedCatalog || {};
  const optionsFor = (field) => {
    if (field.options) return field.options;
    if (field.options_key === "traditions")
      return (cat.traditions || []).map((t) => t.name);
    return cat[field.options_key] || [];
  };

  const toggle = (key) => (option) => {
    const arr = Array.isArray(d[key]) ? d[key] : d[key] ? [String(d[key])] : [];
    update(
      key,
      arr.includes(option) ? arr.filter((x) => x !== option) : [...arr, option],
    );
  };

  const setSeason = (season, val) => {
    const cur = (d.seasonal_customs && typeof d.seasonal_customs === "object") ? d.seasonal_customs : {};
    update("seasonal_customs", { ...cur, [season]: val });
  };

  const renderField = (f) => {
    const v = d[f.key];
    if (f.type === "select") {
      const opts = optionsFor(f);
      return (
        <div className="pp-field" key={f.key}>
          <label>{f.label}</label>
          <select
            value={v || ""}
            onChange={(e) => update(f.key, e.target.value)}
            data-testid={`mr-${f.key}`}
          >
            <option value="">—</option>
            {opts.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
          {f.help && <div className="pp-help">{f.help}</div>}
        </div>
      );
    }
    if (f.type === "chips") {
      const opts = optionsFor(f);
      return (
        <div className="pp-field full" key={f.key}>
          <label>{f.label}</label>
          <Chips k={f.key} options={opts} value={v} onToggle={toggle(f.key)} />
          {f.help && <div className="pp-help">{f.help}</div>}
        </div>
      );
    }
    if (f.type === "resource_multi") {
      const opts = (cat.resources || []).map((r) => r.name || r.id);
      return (
        <div className="pp-field full" key={f.key}>
          <label>{f.label}</label>
          <Chips k={f.key} options={opts} value={v} onToggle={toggle(f.key)} />
          {f.help && <div className="pp-help">{f.help}</div>}
        </div>
      );
    }
    if (f.type === "textarea") {
      return (
        <div className="pp-field full" key={f.key}>
          <label>{f.label}</label>
          <textarea
            value={v || ""}
            placeholder={f.placeholder || ""}
            rows={4}
            onChange={(e) => update(f.key, e.target.value)}
            data-testid={`mr-${f.key}`}
          />
        </div>
      );
    }
    if (f.type === "seasonal") {
      const seasons = cat[f.seasons_key] || [];
      const current = (v && typeof v === "object" && !Array.isArray(v)) ? v : {};
      return (
        <div className="pp-field full" key={f.key}>
          <label>{f.label}</label>
          <div className="pp-seasonal">
            {seasons.map((s) => (
              <div key={s} className="pp-seasonal-row">
                <div className="pp-seasonal-label">{s}</div>
                <input
                  type="text"
                  className="pp-seasonal-input"
                  value={current[s] || ""}
                  placeholder={f.placeholder || ""}
                  onChange={(e) => setSeason(s, e.target.value)}
                  data-testid={`mr-seasonal-${slug(s)}`}
                />
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="pp-music-resources" data-testid="pp-music-resources">
      {err && (
        <div className="pp-error" data-testid="mr-catalog-error">{err}</div>
      )}
      {MUSIC_RESOURCES_FIELDS.map((section) => (
        <div className="pp-mr-section" key={section.section}>
          <h3>{section.section}</h3>
          <div className="pp-grid">
            {section.fields.map(renderField)}
          </div>
        </div>
      ))}
      <div className="pp-mr-note">
        Music Resources feeds the Musical Intelligence recommendation engine.
        This tab is additive — existing profile fields are unchanged.
      </div>
    </div>
  );
}
